import { StyleSheet } from 'react-native-unistyles';
import { Box, Text } from '@/components/ui/primitives';
import { Badge } from '@/components/ui/Badge';
import { Card, CardContent } from '@/components/ui/Card';
import { Icon } from '@/components/ui/icon';
import { Avatar, AvatarImage, AvatarFallback, GradientAvatarFallback } from '@/components/ui/Avatar';
import { Clock, Medal, Trophy, Star } from 'lucide-react-native';
import { colors } from '@/lib/theme';

export const MMClockBanner = ({
  onTheClock,
  displayName,
  pickNumber,
  roundNumber,
}: {
  onTheClock: boolean;
  displayName: string | null;
  pickNumber: number;
  roundNumber: number | null;
}) => {
  return (
    <Card style={styles.clockCard(onTheClock)}>
      <CardContent style={styles.clockContent}>
          <Box style={styles.iconBlock(onTheClock)}>
             <Icon as={Clock} color={colors.text.primary} size={22} />
          </Box>
          <Box flex={1}>
            <Text
              variant="title"
              style={styles.clockTitle}
              fontWeight="800"
            >
              {onTheClock ? 'YOUR TURN TO PICK!' : `ON THE CLOCK: ${displayName?.toUpperCase()}`}
            </Text>
            <Box flexDirection="row" alignItems="center" gap="xs">
               <Badge variant={onTheClock ? 'outline' : 'secondary'} style={onTheClock ? styles.whiteBadge : undefined}>
                 <Text variant="caption" fontWeight="800" color="text">
                    PICK #{pickNumber}
                 </Text>
               </Badge>
               {Boolean(roundNumber) && (
                 <Text
                    variant="caption"
                    style={styles.clockSub(onTheClock)}
                    fontWeight="700"
                 >
                   ROUND {roundNumber}
                 </Text>
               )}
            </Box>
          </Box>
        </CardContent>
    </Card>
  );
};

export const MMRankBadge = ({ rank }: { rank: number }) => {
  const getRankStyle = () => {
    switch (rank) {
      case 1: return { bg: colors.ink.acid, icon: Trophy, color: colors.text.primary };
      case 2: return { bg: colors.ink.paperDark, icon: Medal, color: colors.text.primary };
      case 3: return { bg: colors.warning[300], icon: Medal, color: colors.text.primary };
      default: return { bg: colors.neutral[100], icon: Star, color: colors.text.secondary };
    }
  };

  const style = getRankStyle();

  return (
    <Box
      width={40}
      height={40}
      backgroundColor={style.bg as any}
      alignItems="center"
      justifyContent="center"
      style={styles.rankBadge}
    >
      {rank <= 3 ? (
        <Icon as={style.icon} color={style.color} size={20} />
      ) : (
        <Text variant="bodySmall" fontWeight="800" color={style.color}>#{rank}</Text>
      )}
    </Box>
  );
};

export const MMRoundBadge = ({ label, value, isFinal }: { label: string; value: number; isFinal: boolean }) => {
  const style = { opacity: isFinal ? 1 : 0.7 };
  return (
  <Box
    backgroundColor="surface"
    borderWidth={1}
    borderColor="border"
    borderRadius="none"
    paddingHorizontal="m"
    paddingVertical="s"
    minWidth={80}
    alignItems="center"
    style={style}
  >
    <Text variant="caption" fontWeight="800" color="mutedForeground" numberOfLines={1}>
      {label.toUpperCase()}
    </Text>
    <Text variant="title" fontWeight="900" color={value > 0 ? 'text' : 'mutedForeground'}>
      {value}
    </Text>
  </Box>
  );
};

export const MMDraftHeader = ({
  displayName,
  isUser,
  isCurrent,
  profileImageUrl,
}: {
  displayName: string;
  isUser: boolean;
  isCurrent: boolean;
  profileImageUrl?: string | null;
}) => {
  const initials = displayName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();

  return (
    <Box
      width={160}
      paddingVertical="m"
      paddingHorizontal="s"
      backgroundColor={isCurrent ? 'acid' : 'muted'}
      alignItems="center"
      borderRightWidth={1}
      borderColor="border"
      gap="xs"
      style={isCurrent ? styles.currentDraftHeader : undefined}
    >
      <Box flexDirection="row" alignItems="center" gap="xs">
        <Avatar size={44} style={[styles.draftAvatar, isCurrent && styles.currentDraftAvatar]}>
          {profileImageUrl ? (
            <>
              <AvatarImage src={profileImageUrl} />
              <AvatarFallback>
                 <GradientAvatarFallback name={displayName}>{initials}</GradientAvatarFallback>
              </AvatarFallback>
            </>
          ) : (
            <GradientAvatarFallback name={displayName}>{initials}</GradientAvatarFallback>
          )}
        </Avatar>
      </Box>
      <Text
        variant="caption"
        fontWeight="800"
        numberOfLines={1}
        style={styles.draftName}
      >
        {displayName.toUpperCase()}
      </Text>
      {isUser && (
        <Badge variant="outline" style={styles.youBadge}>
          <Text variant="caption" fontSize={8} fontWeight="900" color="text">YOU</Text>
        </Badge>
      )}
    </Box>
  );
};

const styles = StyleSheet.create((theme) => ({
  clockCard: (onTheClock: boolean) => ({
    backgroundColor: onTheClock ? theme.colors.acid : theme.colors.paperDark,
    borderColor: theme.colors.press,
    borderRadius: 0,
    borderWidth: 3,
    elevation: 4,
    overflow: 'hidden',
    shadowColor: theme.colors.press,
    shadowOffset: { width: 8, height: 8 },
    shadowOpacity: 1,
    shadowRadius: 0,
  }),
  clockContent: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing.m,
    paddingVertical: theme.spacing.m,
  },
  clockSub: (onTheClock: boolean) => ({
    color: onTheClock ? theme.colors.press : theme.colors.textSecondary,
    fontFamily: theme.typography.fontFamily.mono,
    fontSize: 12,
  }),
  clockTitle: {
    color: theme.colors.text,
    fontFamily: theme.typography.fontFamily.display,
    fontSize: 18,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  currentDraftAvatar: {
    borderColor: theme.colors.press,
  },
  currentDraftHeader: {
    backgroundColor: theme.colors.acid,
  },
  draftAvatar: {
    borderColor: theme.colors.border,
    borderRadius: 0,
    borderWidth: 2,
  },
  draftName: {
    color: theme.colors.text,
  },
  iconBlock: (onTheClock: boolean) => ({
    alignItems: 'center',
    backgroundColor: onTheClock ? theme.colors.paper : theme.colors.background,
    borderColor: theme.colors.press,
    borderRadius: 0,
    borderWidth: 2,
    height: 44,
    justifyContent: 'center',
    width: 44,
  }),
  rankBadge: {
    borderColor: theme.colors.press,
    borderRadius: 0,
    borderWidth: 2,
    elevation: 2,
    shadowColor: theme.colors.press,
    shadowOffset: { width: 4, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 0,
  },
  whiteBadge: {
    backgroundColor: theme.colors.paper,
    borderColor: theme.colors.press,
  },
  youBadge: {
    borderColor: theme.colors.press,
  },
}));
